import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import type { Dispatch, ForwardedRef, ReactNode, SetStateAction } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconSearchOutlined from '~icons/ant-design/search-outlined';
import IconX from '~icons/bx/x';

import { HighlightText } from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';
import {
	useForwardingRef,
	useHighlightMeta,
} from '@adflow/frontend/util-common';
import { tokenizeSearchString } from '@adflow/shared/util-js';

import { FormControl } from '../FormControl';

import type { InputProps } from './Input';
import { Input } from './Input';

type CollapsibleOption =
	| 'never'
	| 'always'
	| 'media-query'
	| { type: 'media-query'; queryWidth: string };

const collapsedWidth = 44;
const collapsedWidthWithEndAdorn = 78;
const collapsedWidthWithClear = 100;

/**
 * If option is 'always', always apply style
 * If option is 'media-query', only apply style in
 * the query
 */
const collapsibleOption = (
	collapsible: CollapsibleOption,
	style: CSSInterpolation
) => {
	if (collapsible === 'always') {
		return style;
	}

	if (collapsible === 'media-query') {
		return { [`@media (max-width: ${theme.screens.xl})`]: style };
	}

	if (collapsible === 'never') {
		return null;
	}

	return {
		[`@media (max-width: ${collapsible.queryWidth})`]: style,
	};
};

const styles = {
	positionAnchor: (collapsible: CollapsibleOption, showEndAdornment: boolean) =>
		css(
			collapsibleOption(
				collapsible,
				css({
					position: 'relative',
					height: '2rem',
					minWidth: showEndAdornment
						? collapsedWidthWithEndAdorn
						: collapsedWidth,
				})
			),
			{ maxWidth: '100%' }
		),

	positionContainer: (collapsible: CollapsibleOption) =>
		css(
			{ position: 'relative', right: '0px' },
			collapsibleOption(collapsible, { position: 'absolute' })
		),
	container: (
		collapsible: CollapsibleOption,
		expanded: boolean,
		showEndAdornment: boolean,
		value: InputProps['value'],
		customWidth?: number
	) => {
		const width =
			customWidth && expanded
				? customWidth
				: expanded
					? 320
					: showEndAdornment && value
						? collapsedWidthWithClear
						: showEndAdornment
							? collapsedWidthWithEndAdorn
							: collapsedWidth;

		return css(
			su.roundedFull,
			{
				height: '2rem',

				transition: 'width 0.3s ease-in-out',
			},
			collapsibleOption(
				collapsible,
				css({
					width,
				})
			)
		);
	},

	input: (
		collapsible: CollapsibleOption,
		expanded: boolean,
		showEndAdornment: boolean
	) =>
		css(
			{ padding: '0px', marginLeft: '0.5rem', marginRight: '1rem' },
			collapsibleOption(
				collapsible,
				!expanded && showEndAdornment && { minWidth: '0px', margin: 0 }
			)
		),
	endAdornment: (
		collapsible: CollapsibleOption,
		expanded: boolean,
		showEndAdornment: boolean
	) =>
		css(
			su.spaceX('0.25rem'),
			{ display: 'flex', alignItems: 'center' },
			collapsibleOption(
				collapsible,
				css(
					!expanded && !showEndAdornment && { display: 'none' },
					!expanded && showEndAdornment && { marginLeft: '0.5rem' }
				)
			)
		),

	clearIcon: (value: InputProps['value'], hideClear: boolean) =>
		css(
			{ marginRight: '-0.25rem' },
			(!value || hideClear) && { display: 'none' }
		),
};

export type InputSearchProps = InputProps & {
	stacked?: boolean;
	handleClear?: () => void;
	hideClear?: boolean;
	barcodeScanner?: ReactNode;
	collapsible?: CollapsibleOption;

	forceShowEndAdornment?: boolean;

	helperText?: (expanded: boolean) => ReactNode;
	customWidth?: number;
};

export function InputSearch(props: InputSearchProps) {
	const [expanded, setExpanded] = useState(false);
	return (
		<InputSearchUncontrolledExpand
			{...props}
			expanded={expanded}
			setExpanded={setExpanded}
		/>
	);
}

export type InputSearchUncontrolledExpandProps = InputSearchProps & {
	expanded: boolean;
	setExpanded: Dispatch<SetStateAction<boolean>>;
	customWidth?: number;
};

/**
 * By default, most pages do not need to control the expanded
 * state of the InputSearch. But for cases where the input bar
 * is default collapsed, there are cases where the parent will
 * want to manually control the expanded state.
 */
export const InputSearchUncontrolledExpand = forwardRef(
	function InputSearchUncontrolledExpandRender(
		props: InputSearchUncontrolledExpandProps,
		forwardedRef: ForwardedRef<HTMLInputElement>
	) {
		const { value } = props;
		const {
			stacked,
			handleClear,
			hideClear = false,
			placeholder,
			barcodeScanner,
			collapsible = 'never',
			helperText,
			expanded,
			setExpanded,
			forceShowEndAdornment = false,
			customWidth,
			...inputProps
		} = props;

		const { t } = useTranslation();

		const { highlightedText } = useHighlightMeta();

		const isClearing = useRef(false);
		const iRef = useRef<HTMLInputElement>(null);

		const inputRef = useForwardingRef(null, [iRef, forwardedRef]);

		useEffect(() => {
			/**
			 * In the event that the input is collapsed by controlling
			 * component, make sure to blur the input
			 */
			if (!expanded) {
				inputRef.current?.blur();
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [expanded]);

		const input = (
			<Input
				ref={inputRef}
				startAdornment={
					<>
						<div
							onClick={() => inputRef?.current?.focus()}
							css={{ cursor: 'pointer' }}
							data-testid="search-icon"
						>
							<IconSearchOutlined
								css={{
									color: theme.colors.text.tableHeader,
								}}
								width={20}
								height={20}
							/>
						</div>
						{stacked &&
						typeof value === 'string' &&
						value.length > 0 &&
						tokenizeSearchString(_.escapeRegExp(value)).length > 1 ? (
							<HighlightText
								css={{
									padding: '0.22rem 0.22rem',
									maxHeight: 32,
									transform: 'translateY(-50%)',
									position: 'absolute',
									marginLeft: '1.5rem',
									marginRight: '1.5rem',
									pointerEvents: 'none',
									top: '50%',
								}}
								text={highlightedText}
								highlight={highlightedText}
								stacked
							/>
						) : null}
					</>
				}
				endAdornment={
					<div
						css={styles.endAdornment(
							collapsible,
							expanded,
							forceShowEndAdornment
						)}
					>
						{barcodeScanner}
						<div
							onMouseDown={(event) => {
								if (event.button !== 0) return; // Only handle left-click
								if (handleClear) {
									isClearing.current = true;
									handleClear();
									isClearing.current = false;
								}
							}}
							css={styles.clearIcon(value, hideClear)}
							data-testid="clear-search-icon"
						>
							<IconX
								css={{
									color: theme.colors.text.tableHeader,
									cursor: 'pointer',
								}}
								width={20}
								height={20}
							/>
						</div>
					</div>
				}
				placeholder={placeholder ?? t('common:search')}
				{...inputProps}
				containerStyle={[
					styles.container(
						collapsible,
						expanded,
						forceShowEndAdornment,
						value,
						customWidth
					),
					inputProps.containerStyle,
				]}
				inputStyle={[
					styles.input(collapsible, expanded, forceShowEndAdornment),
					inputProps.inputStyle,
				]}
				onFocus={(e) => {
					setExpanded(true);
					inputProps.onFocus?.(e);
				}}
				onBlur={(e) => {
					if (isClearing.current) {
						return;
					}
					setExpanded(false);
					inputProps.onBlur?.(e);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						setExpanded(false);
					}
					inputProps.onKeyDown?.(e);
				}}
			/>
		);

		return (
			<div css={styles.positionAnchor(collapsible, forceShowEndAdornment)}>
				<div css={styles.positionContainer(collapsible)}>
					{helperText ? (
						<FormControl
							helperText={helperText(expanded)}
							helperStyle={{
								position: 'absolute',
								bottom: '-16px',
								height: '16px',
								left: 16,
							}}
							helperTextStyle={{
								color: theme.colors.text.secondary,
								textAlign: 'right',
							}}
						>
							{input}
						</FormControl>
					) : (
						input
					)}
				</div>
			</div>
		);
	}
);
